/* eslint-disable react-hooks/rules-of-hooks */
import { FC, useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { CalendarEvent } from '@emotion-icons/boxicons-solid/CalendarEvent'
import { Chip, List, ListItem, Popover, Typography } from '@mui/material'
import { DateCalendar, LocalizationProvider, DateView } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { Colors, Shapes, Typographies, useTypography } from 'src/components/theme'
import { ButtonOutlined } from 'src/components/button/outlined'
import { getDateWithoutBloodyTZ } from 'src/utils/date-utils'
import { ChevronDown } from '@emotion-icons/fa-solid/ChevronDown'
import { Flex } from 'src/components/flex-containers'
import { ButtonSolid } from 'src/components/button/solid'
import { wait } from 'src/utils/wait'
import { commonDataRangeShortCuts } from 'src/components/date-range/utils'

const ButtonWrapper = styled(Flex)`
  margin-top: ${Shapes.spacing[4]}px;
  padding: ${Shapes.spacing[2]}px;
  gap: ${Shapes.spacing[2]}px;
  justify-content: space-between;
`

const DateButton = styled(ButtonOutlined)`
  ${useTypography(Typographies.text1)}
  color:${Colors.textBlack};
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[2]}px;
  border-color: ${Colors.borderInput};
  align-items: center;
  justify-content: space-between;
  min-width: 145px;
`

const ButtonLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[1]}px;
  padding-right: ${Shapes.spacing[4]}px;
`

const StyledPopover = styled(Popover)<{ single: number }>`
  .MuiPaper-root {
    padding-top: ${({ single }) => (single ? '16' : '0')}px;
    background-color: ${Colors.bgWhite};
  }
`

const Container = styled.div`
  display: flex;
`

const StyledDateCalendar = styled(DateCalendar)`
  height: auto;

  button.MuiIconButton-root {
    border: none;
  }
  button {
    ${useTypography(Typographies.textmdm)}
    border-radius: ${Shapes.borderRadius}px;
    border: 1px solid transparent;
    :hover {
      background-color: transparent;
      border-color: ${Colors.blueDark};
    }
  }
  button.MuiPickersMonth-monthButton.Mui-disabled {
    color: ${Colors.textLight};
    text-decoration: line-through;
  }
`

type DatePickerProps = {
  label?: string
  date: Date
  onDateChange: (value: Date | null) => void
  onShortcutSelect?: (value: [Date, Date]) => void
  minDate?: Date
  maxDate?: Date
  renderLabel?: (date: Date) => string
  views: DateView[]
  openTo: DateView
}
export const DatePicker: FC<DatePickerProps> = ({
  label,
  date,
  onDateChange,
  onShortcutSelect,
  minDate = getDateWithoutBloodyTZ('2019-01-01'),
  maxDate = new Date(),
  renderLabel,
  openTo,
  views,
}) => {
  const [internalDate, setInternalDate] = useState<Date>(date)

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const renderedlabel = useMemo(() => {
    if (renderLabel) {
      return renderLabel(date)
    }

    if (openTo === 'month') {
      return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`
    }

    if (openTo === 'year') {
      return `${date.getFullYear()}`
    }

    // right now we only support month and year
    return 'TODO'
  }, [renderLabel, date, openTo])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const onInternalDateChange = (value: Date | null) => {
    if (!value || value < minDate || value > maxDate) {
      return
    }
    setInternalDate(value)
  }

  const onApply = async () => {
    handleClose()
    await wait(100)
    onDateChange(internalDate)
  }

  const onCancel = () => {
    handleClose()
    setInternalDate(date)
  }

  const handleClickShortcut = (range: [Date, Date]) => async () => {
    handleClose()
    await wait(100)
    onShortcutSelect?.(range)
  }

  useEffect(() => {
    setInternalDate(date)
  }, [date])

  return (
    <>
      <DateButton aria-describedby={id} variant="contained" onClick={handleClick}>
        <ButtonLabel>
          <CalendarEvent size={14} color={Colors.textMedium} />
          {label && (
            <Typography variant="text1" color={Colors.textMedium}>
              {label}
            </Typography>
          )}
          {renderedlabel}
        </ButtonLabel>
        <ChevronDown size={14} color={Colors.blueDark} />
      </DateButton>
      <StyledPopover
        id={id}
        open={open}
        single={views.length === 1 ? 1 : 0}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}>
        <Container>
          {onShortcutSelect && (
            <List dense>
              {commonDataRangeShortCuts.map((shortcut) => (
                <ListItem>
                  <Chip
                    label={shortcut.label}
                    onClick={handleClickShortcut(shortcut.getValue({ isValid: () => true }))}
                  />
                </ListItem>
              ))}
            </List>
          )}
          <div>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StyledDateCalendar
                value={internalDate}
                onChange={onInternalDateChange}
                minDate={minDate}
                maxDate={maxDate}
                openTo={openTo}
                views={views}
              />
            </LocalizationProvider>

            <ButtonWrapper>
              <ButtonOutlined onClick={onCancel}>Cancel</ButtonOutlined>
              <ButtonSolid onClick={onApply}>Apply</ButtonSolid>
            </ButtonWrapper>
          </div>
        </Container>
      </StyledPopover>
    </>
  )
}
